import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { UserPlus, Mail, ArrowRight, RefreshCw } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useLogo } from '../context/LogoContext';
import LanguageSelector from '../components/LanguageSelector';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function RegisterPage() {
  const { t } = useLanguage();
  const { logoUrl } = useLogo();
  const [step, setStep] = useState('register'); // 'register' | 'verify' | 'done'
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [email, setEmail] = useState('');
  const [formData, setFormData] = useState({
    full_name: '', email: '', phone: '', password: '', confirmPassword: '', referral_code: ''
  });
  const [code, setCode] = useState('');

  const handleRegister = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      toast.error(t('register_password_mismatch')); return;
    }
    if (formData.password.length < 6) {
      toast.error(t('register_password_min')); return;
    }
    setLoading(true);
    try {
      const { confirmPassword, ...data } = formData;
      const res = await axios.post(`${API_URL}/api/auth/register`, data);
      setEmail(res.data.email || formData.email);
      setStep('verify');
      toast.success(t('verify_code_sent'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('register_failed'));
    } finally { setLoading(false); }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (code.length !== 6) { toast.error(t('verify_code_invalid')); return; }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/verify-email`, { email, code });
      setStep('done');
      toast.success(t('verify_success'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('verify_failed'));
    } finally { setLoading(false); }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await axios.post(`${API_URL}/api/auth/resend-code`, { email });
      toast.success(t('verify_resent'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('verify_resend_failed'));
    } finally { setResending(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-8">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-between mb-6">
          <Link to="/" className="flex items-center gap-3">
            <img src={logoUrl} alt="PRC Remit" className="h-8 w-8 rounded-lg" />
            <span className="font-heading font-bold text-xl text-primary">PRC Remit</span>
          </Link>
          <LanguageSelector />
        </div>

        <div className="bg-card border border-border/50 rounded-2xl p-6 sm:p-8" data-testid="register-card">

          {/* Step 1: Register Form */}
          {step === 'register' && (
            <>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                  <UserPlus className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h1 className="font-heading text-xl font-bold text-primary">{t('register_title')}</h1>
                  <p className="text-xs text-muted-foreground">{t('register_desc')}</p>
                </div>
              </div>
              <form onSubmit={handleRegister} className="space-y-4">
                <div className="space-y-2">
                  <Label>{t('name')} *</Label>
                  <Input value={formData.full_name} onChange={(e) => setFormData({...formData, full_name: e.target.value})} placeholder={t('register_name_placeholder')} required data-testid="register-name" />
                </div>
                <div className="space-y-2">
                  <Label>{t('email')} *</Label>
                  <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="user@example.com" required data-testid="register-email" />
                </div>
                <div className="space-y-2">
                  <Label>{t('phone')}</Label>
                  <Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder="090-1234-5678" data-testid="register-phone" />
                </div>
                <div className="space-y-2">
                  <Label>{t('password')} *</Label>
                  <Input type="password" value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} required data-testid="register-password" />
                </div>
                <div className="space-y-2">
                  <Label>{t('register_confirm_password')} *</Label>
                  <Input type="password" value={formData.confirmPassword} onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})} required data-testid="register-confirm-password" />
                </div>
                <div className="space-y-2">
                  <Label>{t('register_referral_code')}</Label>
                  <Input value={formData.referral_code} onChange={(e) => setFormData({...formData, referral_code: e.target.value})} placeholder={t('register_referral_placeholder')} data-testid="register-referral" />
                </div>
                <Button type="submit" className="w-full rounded-full h-12" disabled={loading} data-testid="register-submit">
                  {loading ? t('register_registering') : t('register_btn')}
                </Button>
              </form>
              <p className="text-center text-sm text-muted-foreground mt-6">
                {t('register_has_account')} <Link to="/login" className="text-accent font-medium hover:underline" data-testid="login-link">{t('login')}</Link>
              </p>
            </>
          )}

          {/* Step 2: Verify Code */}
          {step === 'verify' && (
            <div className="text-center" data-testid="verify-step">
              <div className="w-16 h-16 bg-accent/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Mail className="h-8 w-8 text-accent" />
              </div>
              <h2 className="font-heading text-xl font-bold text-primary mb-2">{t('verify_title')}</h2>
              <p className="text-sm text-muted-foreground mb-2">{t('verify_desc')}</p>
              <p className="text-sm font-medium text-primary mb-6" data-testid="verify-email-display">{email}</p>

              <form onSubmit={handleVerify} className="space-y-4">
                <Input
                  type="text" inputMode="numeric" maxLength={6}
                  value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="text-center text-3xl font-mono tracking-[0.5em] h-16"
                  autoFocus
                  data-testid="verify-code-input"
                />
                <Button type="submit" className="w-full rounded-full h-12" disabled={loading || code.length !== 6} data-testid="verify-submit">
                  {loading ? t('loading') : t('verify_button')}
                </Button>
              </form>

              <div className="mt-6 pt-4 border-t border-border">
                <p className="text-xs text-muted-foreground mb-3">{t('verify_no_code')}</p>
                <Button variant="ghost" size="sm" onClick={handleResend} disabled={resending} className="gap-2" data-testid="resend-code-btn">
                  <RefreshCw className={`h-3.5 w-3.5 ${resending ? 'animate-spin' : ''}`} />
                  {resending ? t('loading') : t('verify_resend')}
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Done */}
          {step === 'done' && (
            <div className="text-center py-4" data-testid="verify-done">
              <div className="w-16 h-16 bg-emerald-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg className="h-8 w-8 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
              </div>
              <h2 className="font-heading text-xl font-bold text-primary mb-2">{t('verify_complete_title')}</h2>
              <p className="text-sm text-muted-foreground mb-6">{t('verify_complete_desc')}</p>
              <Link to="/login">
                <Button className="rounded-full h-12 px-8 gap-2" data-testid="go-to-login-btn">
                  {t('login')} <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
